import { createApp } from "./main";
import { markHydrated } from "./core/utils/hydration";
import { installConsentManager } from "./core/privacy/consent";
import { installOptionalAnalyticsScheduler } from "./core/utils/optionalAnalytics";
import { installWebVitals } from "./core/utils/webVitals";
import { nextTick } from "vue";
import { createScrollRestoreGuard } from "./core/utils/scrollRestoreGuard";
import {
  classifyNavigation,
  isHomeDestinationReady,
  isNavigationKind,
  isQueryOnlyNavigation,
  shouldNavigateWithoutRouteTransition,
  shouldUseRouteLoadingCover,
} from "./core/utils/navigationTransition";

// Install the small first-party consent manager before mounting Vue. It does
// not run category-gated vendor tags unless the current choice allows them.
const markAnalyticsReady = installOptionalAnalyticsScheduler();
installConsentManager();
installWebVitals();

// Detect browser Back/Forward navigation and flag it in sessionStorage.
if (typeof window !== "undefined") {
  window.addEventListener("popstate", () => {
    sessionStorage.setItem("vf_back", "1");
    const sourceFullPath = window.__vfActiveFullPath || "";
    const targetFullPath = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    const sourceSnapshot = captureHistoryInfoSourceSnapshot(sourceFullPath);
    delete document.documentElement.dataset.vfInfoReadyPath;
    const sourceContentIdentity = routeContentIdentity();
    if (sourceContentIdentity) {
      sourceContentIdentity.pathname = String(
        sourceFullPath || window.location.pathname
      ).split(/[?#]/, 1)[0];
    }
    // This listener is registered before Vue Router creates its own history
    // listener. Capture the page being left while its DOM and scroll position
    // are still intact; later router hooks otherwise see the target page and
    // can accidentally save the target's top position as the source position.
    window.__vfEarlyHistoryNavigation = {
      sourceFullPath,
      sourceSnapshot,
      sourceSnapshotCommitted: false,
      sourceHistoryPosition:
        window.__vfActiveHistoryPosition !== null &&
        window.__vfActiveHistoryPosition !== undefined &&
        Number.isFinite(Number(window.__vfActiveHistoryPosition))
          ? Number(window.__vfActiveHistoryPosition)
          : null,
      sourceScroll: {
        left: window.scrollX || 0,
        top: window.scrollY || 0,
      },
      sourceWasProduct: !!document.querySelector('[data-page-kind="product"]'),
      sourceContentIdentity,
      targetFullPath,
      targetHistoryPosition:
        window.history?.state?.position !== null &&
        window.history?.state?.position !== undefined &&
        Number.isFinite(Number(window.history.state.position))
          ? Number(window.history.state.position)
          : null,
    };
  });
}

// TEMP diagnostic — horizontal overflow detector. Open any page with
// "#vfoverflow" in the URL: elements wider than the viewport get a red
// outline and are listed in a panel (selector + how many px they stick out).
// Dev-only; auto-stops after MAX_REPORTS to avoid production perf drain.
if (
  typeof window !== "undefined" &&
  import.meta.env.DEV &&
  /vfoverflow/i.test(window.location.href)
) {
  // An element only widens the document if it sticks out on the RIGHT (in
  // LTR), is not position:fixed, and no ancestor clips it (any overflow-x
  // other than "visible" contains it). Everything else — marquee tracks
  // inside overflow-hidden strips, the left:-9999px skip-link — is a false
  // positive and is filtered out.
  const extendsDocument = (el) => {
    if (getComputedStyle(el).position === "fixed") return false;
    let p = el.parentElement;
    while (p && p !== document.documentElement) {
      const cs = getComputedStyle(p);
      // A fixed ANCESTOR takes the whole subtree out of document flow, and a
      // clipping ancestor contains the overflow — either way this element
      // cannot widen the document.
      if (cs.position === "fixed") return false;
      const ox = cs.overflowX;
      if (ox && ox !== "visible") return false;
      p = p.parentElement;
    }
    return true;
  };

  const describe = (el) => {
    const c =
      typeof el.className === "string"
        ? "." + el.className.split(" ").filter(Boolean).slice(0, 2).join(".")
        : "";
    return `${el.tagName.toLowerCase()}${c}`.slice(0, 44);
  };

  // Ancestor trail of the worst offender, so the structure is unambiguous.
  const chain = (el) => {
    const parts = [];
    let cur = el;
    for (let i = 0; cur && cur !== document.body && i < 5; i++) {
      parts.push(describe(cur));
      cur = cur.parentElement;
    }
    return parts.join("\n   < ");
  };

  const report = () => {
    const vw = document.documentElement.clientWidth;
    const offenders = [];
    document.querySelectorAll("body *").forEach((el) => {
      if (el.id === "vf-overflow-panel") return;
      const r = el.getBoundingClientRect();
      if (r.width > 0 && r.right > vw + 1 && extendsDocument(el)) {
        // skip elements whose PARENT is already reported (list roots only)
        if (
          el.parentElement &&
          offenders.some((o) => o.el === el.parentElement)
        )
          return;
        offenders.push({
          el,
          over: Math.round(r.right - vw),
        });
      }
    });
    offenders.sort((a, b) => b.over - a.over);
    let panel = document.getElementById("vf-overflow-panel");
    if (!panel) {
      panel = document.createElement("div");
      panel.id = "vf-overflow-panel";
      panel.setAttribute(
        "style",
        "position:fixed;left:0;right:0;bottom:0;z-index:2147483647;background:rgba(0,0,0,.85);color:#7cfc00;font:10px/1.4 monospace;padding:6px 8px;pointer-events:none;white-space:pre;max-height:40vh;overflow:hidden"
      );
      document.body.appendChild(panel);
    }
    panel.textContent =
      `docW=${document.documentElement.scrollWidth} vw=${vw}\n` +
      (offenders.length
        ? offenders
            .slice(0, 6)
            .map((o) => {
              o.el.style.outline = "2px solid red";
              return `+${o.over}px ${describe(o.el)}`.slice(0, 60);
            })
            .join("\n") +
          `\n── worst offender chain:\n   ${chain(offenders[0].el)}`
        : "no horizontal overflow (docW may exceed vw via absolute elements)");
  };
  const MAX_REPORTS = 20;
  let reportCount = 0;
  const intervalId = window.setInterval(() => {
    report();
    reportCount += 1;
    if (reportCount >= MAX_REPORTS) {
      window.clearInterval(intervalId);
    }
  }, 1500);
}

// ────────────────────────────────────────────────────────────────────────────
// Luxury page crossfade for static "info" / footer pages (About Us, Shipping
// Info, Privacy Policy, Size Guide, …). Uses the native View Transitions API to
// dissolve the page the user is *currently looking at* into the new one.
//
// Why intercept the click instead of using a router guard: the router setup runs
// a synchronous window.scrollTo(0, 0) on forward navigation to non-catalog
// pages. If the transition starts inside/after a router guard, that scroll has
// already happened, so the "old" snapshot is the top of the page (the home hero)
// and the crossfade dissolves from the top — exactly the flash we want to avoid.
// Starting the transition in a capture-phase click handler, before any
// navigation, captures the real on-screen view (e.g. the footer). The
// scroll-to-top then happens *underneath* the frozen snapshot and is never seen.
//
// Sequencing with the page's own entrance: every info page plays an intro fade
// (*PageAppear) plus a .reveal slide (translateY(32px) → 0, triggered on mount
// by an IntersectionObserver). If that slide runs *during* the crossfade the
// View Transition freezes it mid-flight and the live DOM jumps on handoff — the
// "slides up, then slides up again" effect. So we hold the above-the-fold
// reveals at their start state for the snapshot, then release them once the
// crossfade has finished — the page dissolves in, then the content slides up
// cleanly. Below-the-fold reveals are left to animate on scroll as designed.
//
// Falls back to the default instant navigation where the API is unavailable or
// the user prefers reduced motion.
// ────────────────────────────────────────────────────────────────────────────
const ROUTE_TRANSITION_SLOW_MS = 700;
const ROUTE_DESTINATION_READY_MAX_MS = 8000;
const FORWARD_PRODUCT_TOP_HOLD_MS = 650;
const ROUTE_SOURCE_FADE_MS = 300;
const ROUTE_SOURCE_PREPAINT_OPACITY = "0.001";
const ROUTE_COVER_ID = "vf-route-loading-cover";
const ROUTE_SOURCE_SNAPSHOT_ID = "vf-route-source-snapshot";
const STATIC_INFO_READY_PATHS = new Set([
  "/about_us",
  "/cookies_policy",
  "/customer_care",
  "/delivery",
  "/payment_methods",
  "/privacy_policy",
  "/refund_policy",
  "/size_guide",
  "/terms",
  "/work_with_us",
]);

const ensureRouteLoadingCover = () => {
  if (typeof document === "undefined") return null;
  const existing = document.getElementById(ROUTE_COVER_ID);
  if (existing) return existing;

  const cover = document.createElement("div");
  cover.id = ROUTE_COVER_ID;
  cover.className = "vf-route-loading-cover";
  cover.setAttribute("aria-hidden", "true");
  cover.innerHTML =
    '<span class="vf-route-loading-cover__brand">HOOLIGANS</span>' +
    '<span class="vf-route-loading-cover__progress"></span>';
  document.body.appendChild(cover);
  return cover;
};

const setRouteBusy = (busy) => {
  if (typeof document === "undefined") return;
  const app = document.getElementById("app");
  const main = document.getElementById("main-content");
  [app, main].forEach((element) => {
    if (!element) return;
    if (busy) element.setAttribute("aria-busy", "true");
    else element.removeAttribute("aria-busy");
  });
};

const showRouteLoadingCover = () => {
  if (typeof document === "undefined") return;
  ensureRouteLoadingCover();
  const root = document.documentElement;
  root.classList.add("vf-route-cover-visible", "vf-route-cover-immediate");
  setRouteBusy(true);
};

const hideRouteLoadingCover = () => {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  root.classList.remove("vf-route-cover-immediate");
  window.requestAnimationFrame(() => {
    root.classList.remove("vf-route-cover-visible");
    setRouteBusy(false);
  });
};

const removeRouteSourceSnapshot = () => {
  if (typeof document === "undefined") return;
  document.getElementById(ROUTE_SOURCE_SNAPSHOT_ID)?.remove();
};

const waitForCommittedRouteFrame = () =>
  new Promise((resolve) => {
    let finished = false;
    let firstFrame = 0;
    let secondFrame = 0;
    let timeout = 0;
    const finish = () => {
      if (finished) return;
      finished = true;
      if (firstFrame) window.cancelAnimationFrame(firstFrame);
      if (secondFrame) window.cancelAnimationFrame(secondFrame);
      if (timeout) window.clearTimeout(timeout);
      resolve();
    };

    // A forced layout is not a committed paint on mobile Safari. Waiting for
    // the following frame guarantees that the frozen source has reached the
    // screen before the router is allowed to reset scroll or replace content.
    firstFrame = window.requestAnimationFrame(() => {
      firstFrame = 0;
      secondFrame = window.requestAnimationFrame(() => {
        secondFrame = 0;
        finish();
      });
    });
    timeout = window.setTimeout(finish, 240);
  });

const visibleSourceBackground = (preferredSource) => {
  const transparent = new Set(["", "transparent", "rgba(0, 0, 0, 0)"]);
  let element =
    preferredSource ||
    document.elementFromPoint(
      Math.max(0, Math.floor(window.innerWidth / 2)),
      Math.max(0, Math.floor(window.innerHeight / 2))
    );

  while (element) {
    const background = window.getComputedStyle(element).backgroundColor;
    if (!transparent.has(background)) return background;
    element = element.parentElement;
  }

  return window.getComputedStyle(document.body).backgroundColor || "#fff";
};

const captureRouteSourceSnapshot = () => {
  if (typeof document === "undefined") return null;
  const app = document.getElementById("app");
  if (!app) return null;

  const footer = app.querySelector("footer.vf-o-new-footer");
  const footerRect = footer?.getBoundingClientRect();
  const footerCoversViewport =
    footerRect &&
    footerRect.top <= 1 &&
    footerRect.bottom >= window.innerHeight - 1;
  const source = footerCoversViewport ? footer : app;
  const sourceRect = source.getBoundingClientRect();

  removeRouteSourceSnapshot();
  const snapshot = document.createElement("div");
  snapshot.id = ROUTE_SOURCE_SNAPSHOT_ID;
  snapshot.className = "vf-route-source-snapshot";
  snapshot.dataset.vfSnapshotScope = footerCoversViewport ? "footer" : "page";
  snapshot.dataset.vfSnapshotState = "prepainting";
  snapshot.setAttribute("aria-hidden", "true");
  snapshot.setAttribute("inert", "");
  // Keep the live source page visible while Safari rasterizes the clone. An
  // opaque snapshot can otherwise paint its white background one frame before
  // its descendants, producing white -> source page -> fade on browser Back.
  // A non-zero value still asks WebKit to prepaint the composited layer.
  snapshot.style.opacity = ROUTE_SOURCE_PREPAINT_OPACITY;
  snapshot.style.pointerEvents = "none";
  snapshot.style.backgroundColor = visibleSourceBackground(
    footerCoversViewport ? footer : null
  );

  const surface = document.createElement("div");
  surface.className = "vf-route-source-snapshot__surface";
  if (footerCoversViewport) {
    surface.style.top = `${sourceRect.top}px`;
    surface.style.left = `${sourceRect.left}px`;
    surface.style.width = `${sourceRect.width}px`;
    surface.style.minHeight = `${sourceRect.height}px`;
  } else {
    surface.style.width = `${document.documentElement.scrollWidth}px`;
    surface.style.minHeight = `${document.documentElement.scrollHeight}px`;
    surface.style.transform = `translate3d(${-window.scrollX}px, ${-window.scrollY}px, 0)`;
  }

  const clone = source.cloneNode(true);
  clone.removeAttribute("id");
  if (footerCoversViewport) {
    clone.style.margin = "0";
    clone.style.width = "100%";
  }
  surface.appendChild(clone);
  snapshot.appendChild(surface);
  document.body.appendChild(snapshot);
  void snapshot.offsetHeight;
  return snapshot;
};

const commitRouteSourceSnapshot = async (snapshot) => {
  if (!snapshot?.isConnected) return;

  // First let the almost-transparent clone and all of its text/images reach a
  // painted frame while the unchanged live page remains visible underneath.
  await waitForCommittedRouteFrame();
  if (!snapshot.isConnected) return;

  // Swap from the live page to its identical, fully rasterized clone without
  // animating that hand-off. Only the later destination reveal should fade.
  snapshot.style.transition = "none";
  snapshot.style.opacity = "1";
  snapshot.style.pointerEvents = "auto";
  snapshot.dataset.vfSnapshotState = "committed";
  void snapshot.offsetHeight;
  await waitForCommittedRouteFrame();
  if (!snapshot.isConnected) return;

  snapshot.style.removeProperty("transition");
  snapshot.style.removeProperty("opacity");
};

const captureHistoryInfoSourceSnapshot = (sourceFullPath) => {
  const sourcePath = String(sourceFullPath || "")
    .split(/[?#]/, 1)[0]
    .replace(/\/+$/, "");
  if (!STATIC_INFO_READY_PATHS.has(sourcePath || "/")) return null;

  const snapshot = captureRouteSourceSnapshot();
  if (!snapshot) return null;

  const root = document.documentElement;
  root.classList.add("vf-route-transition-active");
  root.dataset.vfRouteKind = "history";
  setRouteBusy(true);
  return snapshot;
};

const fadeRouteSourceSnapshot = (snapshot) =>
  new Promise((resolve) => {
    if (!snapshot?.isConnected) {
      resolve();
      return;
    }
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      snapshot.remove();
      resolve();
      return;
    }

    let finished = false;
    let animation = null;
    const finish = () => {
      if (finished) return;
      finished = true;
      snapshot.removeEventListener("transitionend", handleTransitionEnd);
      animation?.cancel();
      snapshot.remove();
      resolve();
    };
    const handleTransitionEnd = (event) => {
      if (event.target !== snapshot || event.propertyName !== "opacity") return;
      finish();
    };
    window.setTimeout(finish, ROUTE_SOURCE_FADE_MS + 100);

    // Drive the fade through the Web Animations API where available. This
    // keeps the source frame on the compositor on mobile Safari and avoids an
    // occasional one-frame removal when a large cloned page is restyled.
    if (typeof snapshot.animate === "function") {
      animation = snapshot.animate([{ opacity: 1 }, { opacity: 0 }], {
        duration: ROUTE_SOURCE_FADE_MS,
        easing: "cubic-bezier(0.4, 0, 0.2, 1)",
        fill: "forwards",
      });
      animation.finished.then(finish, finish);
      return;
    }

    snapshot.addEventListener("transitionend", handleTransitionEnd);
    window.requestAnimationFrame(() => {
      snapshot.classList.add("vf-route-source-snapshot--leaving");
    });
  });

const routeContentElement = () =>
  document.querySelector("#main-content .vf-page-swipe-shell") ||
  document.getElementById("main-content");

const routeContentIdentity = () => {
  if (typeof document === "undefined") return null;
  const routeContent = routeContentElement();
  const headingText = String(
    routeContent?.querySelector("h1")?.textContent || ""
  )
    .replace(/\s+/g, " ")
    .trim();
  const textSignature = String(routeContent?.textContent || "")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, 240);

  return {
    pathname: window.location.pathname,
    routeRoot: routeContent?.firstElementChild || null,
    headingText,
    textSignature,
  };
};

const hasReplacedPreviousContent = (previousIdentity) => {
  if (!previousIdentity || typeof document === "undefined") return true;
  if (window.location.pathname === previousIdentity.pathname) return false;

  const currentIdentity = routeContentIdentity();
  if (!currentIdentity) return false;
  if (previousIdentity.routeRoot && !previousIdentity.routeRoot.isConnected) {
    return true;
  }

  // The SEO resolver can reuse the same route component for two product URLs.
  // In that case the root element stays connected, but the real destination is
  // ready only once its heading/content no longer belongs to the previous page.
  return (
    currentIdentity.headingText !== previousIdentity.headingText ||
    currentIdentity.textSignature !== previousIdentity.textSignature
  );
};

const hasMeaningfulDestination = (kind, previousIdentity = null) => {
  if (typeof document === "undefined") return true;
  const routeContent = routeContentElement();
  const heading = routeContent?.querySelector("h1");
  const hasHeading = !!heading && !!String(heading.textContent || "").trim();
  const routeText = String(routeContent?.textContent || "")
    .replace(/\s+/g, " ")
    .trim();
  const isErrorPage = /page not found/i.test(
    document.getElementById("main-content")?.textContent || ""
  );
  const pageKind = document
    .querySelector("[data-page-kind]")
    ?.getAttribute("data-page-kind");

  if (kind === "home") {
    return isHomeDestinationReady({
      pathname: window.location.pathname,
      hasHeading,
      hasHomeContent: !!document.querySelector(
        ".vf-o-hero-section, .vf-p-common-home"
      ),
      hasReplacedPreviousContent: hasReplacedPreviousContent(previousIdentity),
      isErrorPage,
    });
  }
  if (kind === "catalog") {
    if (isErrorPage) return true;
    return (
      pageKind === "catalog" &&
      !!document.querySelector(
        '[data-page-kind="catalog"] .catalog-container, [data-page-kind="catalog"] .catalog-product-skeleton-grid'
      )
    );
  }
  if (kind === "product") {
    return (
      pageKind === "product" &&
      hasHeading &&
      hasReplacedPreviousContent(previousIdentity)
    );
  }
  if (kind === "info") {
    // A heading appears before some static-page entrance animations have
    // exposed their actual content. Waiting for a real content payload keeps
    // the View Transition on the source page instead of snapshotting an empty
    // blue/white destination shell.
    const normalizedPath =
      window.location.pathname === "/"
        ? "/"
        : window.location.pathname.replace(/\/+$/, "");
    const staticSeoReady =
      !STATIC_INFO_READY_PATHS.has(normalizedPath) ||
      document.documentElement.dataset.vfInfoReadyPath === normalizedPath;
    return (
      hasHeading &&
      routeText.length > 80 &&
      staticSeoReady &&
      hasReplacedPreviousContent(previousIdentity)
    );
  }
  if (kind === "checkout") {
    return !!document.querySelector(
      '[class*="checkout"], [data-page-kind="checkout"]'
    );
  }
  if (kind === "account" || kind === "search") {
    return hasHeading || !!document.querySelector("#main-content form");
  }
  return hasHeading || !!pageKind || isErrorPage;
};

const waitForMeaningfulDestination = (kind, previousIdentity = null) =>
  new Promise((resolve) => {
    const startedAt = performance.now();
    const check = () => {
      if (
        hasMeaningfulDestination(kind, previousIdentity) ||
        performance.now() - startedAt >= ROUTE_DESTINATION_READY_MAX_MS
      ) {
        resolve();
        return;
      }
      window.setTimeout(check, 40);
    };
    nextTick(check);
  });

const resolveRenderedSeoNavigationKind = (route, previousIdentity) => {
  const fallback = classifyNavigation({
    pathname: route.path,
    routeName: route.name,
  });
  if (String(route.name || "") !== "_slug") return Promise.resolve(fallback);

  return new Promise((resolve) => {
    const startedAt = performance.now();
    const check = () => {
      const pageKind = routeContentElement()
        ?.querySelector("[data-page-kind]")
        ?.getAttribute("data-page-kind");
      if (
        hasReplacedPreviousContent(previousIdentity) &&
        (pageKind === "product" || pageKind === "catalog")
      ) {
        resolve(pageKind);
        return;
      }
      if (performance.now() - startedAt >= ROUTE_DESTINATION_READY_MAX_MS) {
        resolve(fallback);
        return;
      }
      window.setTimeout(check, 40);
    };
    nextTick(check);
  });
};

const navigationAnchor = (event) =>
  event.target instanceof Element ? event.target.closest("a[href]") : null;

const anchorNavigationKind = (anchor) => {
  if (!anchor) return null;
  const explicit = anchor
    .closest("[data-navigation-kind]")
    ?.getAttribute("data-navigation-kind");
  if (isNavigationKind(explicit)) return explicit;
  if (
    anchor.closest(
      ".vf-m-new-product-thumb, .vf-m-product-thumb, [data-product-id]"
    )
  ) {
    return "product";
  }
  return null;
};

/**
 * Covers router.push calls that don't originate from the footer-link capture
 * handler: mobile catalog navigation, search submit, account forms, checkout,
 * callbacks and header links after their close animation.
 */
function setupProgrammaticNavigationTransitions(router) {
  if (typeof window === "undefined") return;

  const originalPush = router.push.bind(router);
  const root = document.documentElement;
  const prefersReducedMotion = () =>
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const supportsViewTransitions = () => "startViewTransition" in document;

  let pendingIntent = null;
  let managedNavigationActive = false;
  let keyboardNavigation = false;
  let historyNavigation = false;
  let historyStartedFromProduct = false;
  let pendingHistoryScroll = null;
  let pendingHistorySourceIdentity = null;
  let pendingForwardNavigation = null;
  let activeHistoryPosition = Number.isFinite(
    Number(window.history?.state?.position)
  )
    ? Number(window.history.state.position)
    : null;
  const historyScrollByPath = new Map();
  const historyScrollByEntry = new Map();

  const currentHistoryPosition = () => {
    const value = Number(window.history?.state?.position);
    return Number.isFinite(value) ? value : null;
  };

  const rememberCurrentScroll = () => {
    const position = { left: window.scrollX || 0, top: window.scrollY || 0 };
    const fullPath = router.currentRoute.value?.fullPath;
    if (fullPath) historyScrollByPath.set(fullPath, position);
    if (activeHistoryPosition !== null) {
      historyScrollByEntry.set(activeHistoryPosition, position);
    }
  };

  window.__vfActiveFullPath = router.currentRoute.value?.fullPath || "";
  window.__vfActiveHistoryPosition = activeHistoryPosition;

  const restoreHistoryScroll = async (
    kind,
    desired,
    previousIdentity = null,
    waitForNaturalHomeHeight = false
  ) => {
    if (!desired) return;
    const guard = createScrollRestoreGuard();
    await waitForMeaningfulDestination(kind, previousIdentity);
    await nextTick();

    return new Promise((resolve) => {
      let attempts = 0;
      let stableFrames = 0;
      const startedAt = performance.now();
      const targetLeft = Number(desired.left) || 0;
      const targetTop = Math.max(0, Number(desired.top) || 0);
      const apply = () => {
        if (!guard.isActive()) {
          guard.dispose();
          resolve();
          return;
        }
        window.scrollTo({ left: targetLeft, top: targetTop });
        const isStable = Math.abs(window.scrollY - targetTop) <= 4;
        // A temporary document-height hold lets Home move to its saved Y before
        // the first paint. Do not call that position stable until the real Home
        // content is tall enough to keep it there after the hold is released.
        const naturalHomeHeightReady =
          !waitForNaturalHomeHeight ||
          targetTop <= 4 ||
          (document.getElementById("app")?.scrollHeight || 0) >=
            targetTop + window.innerHeight - 4;
        stableFrames =
          isStable && naturalHomeHeightReady ? stableFrames + 1 : 0;
        const minimumHoldMs =
          kind === "product"
            ? 450
            : kind === "home" || kind === "catalog"
            ? 350
            : 80;
        if (
          (stableFrames >= 4 &&
            performance.now() - startedAt >= minimumHoldMs) ||
          attempts >= 120
        ) {
          guard.dispose();
          resolve();
          return;
        }
        attempts += 1;
        window.requestAnimationFrame(apply);
      };
      window.requestAnimationFrame(apply);
    });
  };

  const prepositionHistoryHome = (kind, desired) => {
    if (kind !== "home" || !desired) return null;

    const targetLeft = Number(desired.left) || 0;
    const targetTop = Math.max(0, Number(desired.top) || 0);
    const html = document.documentElement;
    const previousMinHeight = html.style.minHeight;

    // Vue mounts the Home shell before its extension sections have restored the
    // page height. Give the browser enough temporary scroll range to put Home
    // at the saved history position in this same navigation task, before it can
    // paint the hero at the top for one frame.
    if (targetTop > 4) {
      const requiredHeight = targetTop + window.innerHeight;
      html.style.minHeight = `${Math.max(html.scrollHeight, requiredHeight)}px`;
    }
    window.scrollTo({ left: targetLeft, top: targetTop });

    return () => {
      if (previousMinHeight) html.style.minHeight = previousMinHeight;
      else html.style.removeProperty("min-height");
      window.scrollTo({ left: targetLeft, top: targetTop });
    };
  };

  const settleForwardProductAtTop = async (navigation) => {
    if (!navigation) return;
    const isCurrentTarget = () =>
      router.currentRoute.value?.fullPath === navigation.fullPath;
    if (!isCurrentTarget()) return;

    const root = document.documentElement;
    root.classList.add("vf-product-top-lock");
    try {
      // Reset immediately, then keep the destination pinned while the reused
      // product component, gallery and late image geometry finish settling.
      // Two frames were not sufficient on slower/mobile renders and allowed a
      // new PDP to inherit the previous product's middle-of-page position.
      window.scrollTo(0, 0);
      await waitForMeaningfulDestination(
        "product",
        navigation.previousIdentity
      );
      await nextTick();

      return await new Promise((resolve) => {
        let attempts = 0;
        let stableFrames = 0;
        const startedAt = performance.now();
        const apply = () => {
          if (!isCurrentTarget()) {
            resolve();
            return;
          }
          window.scrollTo(0, 0);
          stableFrames = Math.abs(window.scrollY) <= 4 ? stableFrames + 1 : 0;
          if (
            (stableFrames >= 4 &&
              performance.now() - startedAt >= FORWARD_PRODUCT_TOP_HOLD_MS) ||
            attempts >= 120
          ) {
            resolve();
            return;
          }
          attempts += 1;
          window.requestAnimationFrame(apply);
        };
        window.requestAnimationFrame(apply);
      });
    } finally {
      root.classList.remove("vf-product-top-lock");
    }
  };

  ensureRouteLoadingCover();

  document.addEventListener(
    "keydown",
    (event) => {
      if (event.key === "Tab" || event.key === "Enter" || event.key === " ") {
        keyboardNavigation = true;
      }
    },
    true
  );
  document.addEventListener(
    "pointerdown",
    () => {
      keyboardNavigation = false;
    },
    true
  );

  // Record semantic intent without taking over the click. Product cards remain
  // owned by the shared-image composable; this intent is only its fallback.
  document.addEventListener(
    "click",
    (event) => {
      const anchor = navigationAnchor(event);
      if (!anchor) return;
      let url;
      try {
        url = new URL(anchor.href, window.location.href);
      } catch {
        return;
      }
      if (url.origin !== window.location.origin) return;
      rememberCurrentScroll();
      pendingIntent = {
        path: `${url.pathname}${url.search}`,
        kind: anchorNavigationKind(anchor),
        createdAt: performance.now(),
      };
    },
    true
  );

  const takeIntent = (fullPath) => {
    const intent = pendingIntent;
    pendingIntent = null;
    if (!intent || performance.now() - intent.createdAt > 1400) return null;
    const intendedPath = String(intent.path || "").replace(/\/$/, "");
    const resolvedPath = String(fullPath || "").replace(/\/$/, "");
    return intendedPath === resolvedPath ? intent.kind : null;
  };

  const focusDestination = () => {
    if (!keyboardNavigation || typeof document === "undefined") return;
    const target = document.querySelector(
      "#main-content h1, #main-content [role='heading'][aria-level='1'], #main-content"
    );
    if (!(target instanceof HTMLElement)) return;
    if (!target.hasAttribute("tabindex")) target.setAttribute("tabindex", "-1");
    target.focus({ preventScroll: true });
    keyboardNavigation = false;
  };
  router.__vfFocusDestination = focusDestination;

  const settleDestination = (kind, previousIdentity = null) =>
    new Promise((resolve) => {
      let settled = false;
      const finish = () => {
        if (settled) return;
        settled = true;
        window.scrollTo(0, 0);
        resolve();
      };
      waitForMeaningfulDestination(kind, previousIdentity).then(finish);
      window.setTimeout(finish, ROUTE_DESTINATION_READY_MAX_MS);
    });

  const finishManagedNavigation = (kind) => {
    hideRouteLoadingCover();
    root.classList.remove("vf-route-transition-active");
    delete root.dataset.vfRouteKind;
    managedNavigationActive = false;
    if (kind !== "checkout") focusDestination();
  };

  const runCoverFallback = async (to, kind) => {
    managedNavigationActive = true;
    root.classList.add("vf-route-transition-active");
    root.dataset.vfRouteKind = kind;
    // Safari does not currently expose the View Transitions API. Showing the
    // fallback cover synchronously made every otherwise-fast catalog visit
    // flash a branded loading page. Match the native-transition path instead:
    // keep the current page visible and only reveal the cover for a genuinely
    // slow navigation.
    const slowTimer = shouldUseRouteLoadingCover(kind)
      ? window.setTimeout(showRouteLoadingCover, ROUTE_TRANSITION_SLOW_MS)
      : 0;
    try {
      const result = await originalPush(to);
      await settleDestination(kind);
      return result;
    } finally {
      if (slowTimer) window.clearTimeout(slowTimer);
      finishManagedNavigation(kind);
    }
  };

  router.push = (to) => {
    let resolved;
    try {
      resolved = router.resolve(to);
    } catch {
      return originalPush(to);
    }

    const current = router.currentRoute.value;
    const previousIdentity = routeContentIdentity();
    if (resolved.fullPath !== current.fullPath) rememberCurrentScroll();
    const explicitKind = takeIntent(resolved.fullPath);
    const kind = classifyNavigation({
      pathname: resolved.path,
      routeName: resolved.name,
      explicitKind,
    });
    const currentKind = classifyNavigation({
      pathname: current.path,
      routeName: current.name,
    });
    if (kind === "info" && resolved.fullPath !== current.fullPath) {
      delete root.dataset.vfInfoReadyPath;
    }
    const zoomOwnsNavigation = document.body.classList.contains("vf-zooming");
    const isDistinctPageNavigation =
      resolved.fullPath !== current.fullPath &&
      !isQueryOnlyNavigation(current.path, resolved.path);
    if (isDistinctPageNavigation) {
      pendingForwardNavigation = {
        fullPath: resolved.fullPath,
        previousIdentity,
        expectedKind: kind,
      };
    }
    const shouldSkip =
      prefersReducedMotion() ||
      managedNavigationActive ||
      root.classList.contains("vf-route-transition-active") ||
      zoomOwnsNavigation ||
      resolved.hash ||
      resolved.fullPath === current.fullPath ||
      isQueryOnlyNavigation(current.path, resolved.path);

    if (shouldSkip) return originalPush(to);
    // Home/catalog links are already router-links. Let that one owner navigate
    // directly: a second document-level transition can overlap the next tap,
    // skip an in-flight View Transition and leave Safari waiting for a route
    // that has already changed.
    if (shouldNavigateWithoutRouteTransition(currentKind, kind)) {
      return originalPush(to);
    }
    if (!supportsViewTransitions()) return runCoverFallback(to, kind);

    managedNavigationActive = true;
    root.classList.add("vf-route-transition-active");
    root.dataset.vfRouteKind = kind;

    let resolveNavigation;
    let rejectNavigation;
    const navigationPromise = new Promise((resolve, reject) => {
      resolveNavigation = resolve;
      rejectNavigation = reject;
    });
    let transition;
    let slowTimer = 0;
    let navigationFinished = false;
    let transitionFinished = false;

    const maybeFinish = () => {
      if (!navigationFinished || !transitionFinished) return;
      if (slowTimer) window.clearTimeout(slowTimer);
      finishManagedNavigation(kind);
    };

    try {
      transition = document.startViewTransition(async () => {
        try {
          const result = await originalPush(to);
          await settleDestination(kind);
          resolveNavigation(result);
          return result;
        } catch (error) {
          rejectNavigation(error);
          throw error;
        }
      });
    } catch {
      managedNavigationActive = false;
      root.classList.remove("vf-route-transition-active");
      delete root.dataset.vfRouteKind;
      return runCoverFallback(to, kind);
    }

    if (shouldUseRouteLoadingCover(kind)) {
      slowTimer = window.setTimeout(() => {
        showRouteLoadingCover();
        if (typeof transition.skipTransition === "function") {
          transition.skipTransition();
        }
      }, ROUTE_TRANSITION_SLOW_MS);
    }

    navigationPromise.then(
      () => {
        navigationFinished = true;
        maybeFinish();
      },
      () => {
        navigationFinished = true;
        maybeFinish();
      }
    );
    transition.finished.then(
      () => {
        transitionFinished = true;
        maybeFinish();
      },
      () => {
        transitionFinished = true;
        maybeFinish();
      }
    );

    return navigationPromise;
  };

  // A popstate event cannot be cancelled, but Vue Router still waits for async
  // navigation guards before replacing the routed DOM. Use that small window to
  // commit the already-created source snapshot to the screen. Without it,
  // mobile Safari can replace a large info page in the same frame in which its
  // clone was appended, briefly exposing the white app canvas before the clone
  // reaches the compositor.
  router.beforeEach(async () => {
    const earlyHistory = window.__vfEarlyHistoryNavigation;
    if (!earlyHistory?.sourceSnapshot || earlyHistory.sourceSnapshotCommitted) {
      return true;
    }

    earlyHistory.sourceSnapshotCommitted = true;
    await commitRouteSourceSnapshot(earlyHistory.sourceSnapshot);
    return true;
  });

  router.afterEach((to, _from, failure) => {
    const earlyHistory = window.__vfEarlyHistoryNavigation;
    const historySourceSnapshot = earlyHistory?.sourceSnapshot || null;
    const clearHistorySourceTransition = () => {
      historySourceSnapshot?.remove();
      if (root.dataset.vfRouteKind !== "history") return;
      root.classList.remove("vf-route-transition-active");
      delete root.dataset.vfRouteKind;
      setRouteBusy(false);
    };
    // scrollBehavior has already consumed this history signal by the time
    // afterEach runs. Clear it synchronously so a quick product-card click on
    // the restored page is always treated as a fresh forward navigation.
    if (earlyHistory) window.__vfEarlyHistoryNavigation = null;
    if (!historyNavigation && !earlyHistory) {
      activeHistoryPosition = currentHistoryPosition();
      window.__vfActiveFullPath = to.fullPath;
      window.__vfActiveHistoryPosition = activeHistoryPosition;
      const forwardNavigation = pendingForwardNavigation;
      pendingForwardNavigation = null;
      if (!failure && forwardNavigation) {
        const finishForwardNavigation = async () => {
          const renderedKind =
            forwardNavigation.expectedKind === "product"
              ? "product"
              : await resolveRenderedSeoNavigationKind(
                  to,
                  forwardNavigation.previousIdentity
                );
          if (renderedKind !== "product") return;
          await settleForwardProductAtTop({
            ...forwardNavigation,
            fullPath: to.fullPath,
          });
        };
        finishForwardNavigation().catch(() => {});
      }
      return;
    }

    // A Back/Forward visit must restore its own saved position; never let a
    // stale explicit-product intent turn that history visit into a scroll reset.
    pendingForwardNavigation = null;

    if (earlyHistory) {
      if (earlyHistory.sourceFullPath) {
        historyScrollByPath.set(
          earlyHistory.sourceFullPath,
          earlyHistory.sourceScroll
        );
      }
      if (earlyHistory.sourceHistoryPosition !== null) {
        historyScrollByEntry.set(
          earlyHistory.sourceHistoryPosition,
          earlyHistory.sourceScroll
        );
      }
      historyStartedFromProduct = earlyHistory.sourceWasProduct;
      pendingHistorySourceIdentity = earlyHistory.sourceContentIdentity || null;
      pendingHistoryScroll =
        historyScrollByEntry.get(earlyHistory.targetHistoryPosition) ||
        historyScrollByPath.get(earlyHistory.targetFullPath) ||
        null;
    }

    historyNavigation = false;
    activeHistoryPosition = currentHistoryPosition();
    window.__vfActiveFullPath = to.fullPath;
    window.__vfActiveHistoryPosition = activeHistoryPosition;
    const desiredScroll = pendingHistoryScroll;
    const historySourceIdentity = pendingHistorySourceIdentity;
    pendingHistoryScroll = null;
    pendingHistorySourceIdentity = null;
    if (failure) {
      clearHistorySourceTransition();
      return;
    }

    const startedFromProduct = historyStartedFromProduct;
    const finishHistoryNavigation = async () => {
      const expectedKind = classifyNavigation({
        pathname: to.path,
        routeName: to.name,
      });
      const releaseHomePosition = prepositionHistoryHome(
        expectedKind,
        desiredScroll
      );
      try {
        // SEO products and catalogs share the same catch-all route name. Wait
        // for its actual rendered page kind before deciding whether the
        // catalog-specific restoration owns this Back/Forward visit.
        const kind = await resolveRenderedSeoNavigationKind(
          to,
          historySourceIdentity
        );
        const productCatalogBack = startedFromProduct && kind === "catalog";
        if (productCatalogBack) return;

        await restoreHistoryScroll(
          kind,
          desiredScroll,
          historySourceIdentity,
          !!releaseHomePosition
        );
        await nextTick();

        if (historySourceSnapshot) {
          // Keep the complete info page over the app until Home/catalog has
          // reached its saved position and that exact frame has been painted.
          // Fading sooner exposes Home at the top for one frame on iOS.
          await waitForCommittedRouteFrame();
          await fadeRouteSourceSnapshot(historySourceSnapshot);
          return;
        }
      } finally {
        releaseHomePosition?.();
        clearHistorySourceTransition();
      }
    };

    // The catalog has its own product-return restoration. Other history
    // destinations use the exact position saved for their history entry, then
    // fade in once that position is stable.
    finishHistoryNavigation().catch(() => {});
  });
}

function setupLuxuryPageCrossfade(router) {
  if (typeof window === "undefined") return;

  const supportsViewTransitions = () => "startViewTransition" in document;
  const prefersReducedMotion = () =>
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  const isInfoPath = (pathname) => {
    try {
      const resolved = router.resolve(pathname);
      return (
        classifyNavigation({
          pathname: resolved.path,
          routeName: resolved.name,
        }) === "info"
      );
    } catch {
      return false;
    }
  };

  // Resolve the eligible same-origin internal target for a click, or null.
  const getCrossfadeTarget = (event) => {
    if (event.defaultPrevented || event.button !== 0) return null;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
      return null;
    }

    const anchor =
      event.target instanceof Element ? event.target.closest("a[href]") : null;
    if (!anchor) return null;

    const targetAttr = anchor.getAttribute("target");
    if (targetAttr && targetAttr !== "_self") return null; // new tab/window
    if (anchor.hasAttribute("download")) return null;
    if ((anchor.getAttribute("rel") || "").includes("external")) return null;

    const rawHref = anchor.getAttribute("href") || "";
    if (
      !rawHref ||
      rawHref.startsWith("#") ||
      rawHref.startsWith("mailto:") ||
      rawHref.startsWith("tel:")
    ) {
      return null;
    }

    let url;
    try {
      url = new URL(anchor.href, window.location.href);
    } catch {
      return null;
    }
    if (url.origin !== window.location.origin) return null;
    if (url.hash) return null; // in-page anchor — let it scroll
    if (url.pathname === window.location.pathname) return null; // same page

    // Header menus close themselves before navigating. Let their single delayed
    // router.push flow through the managed programmatic transition instead of
    // starting a second navigation from this document-level listener.
    if (anchor.closest(".vf-o-new-header, .new-header-menu-panel")) return null;

    // Product cards have a richer shared-image transition owned by the layout.
    // Let that capture listener take the click on both mobile and desktop.
    if (anchorNavigationKind(anchor) === "product") {
      return null;
    }

    return url.pathname + url.search;
  };

  // .reveal sections currently within the viewport (i.e. captured by the snapshot).
  const inViewportReveals = () => {
    const viewportH = window.innerHeight || 0;
    const list = [];
    document.querySelectorAll(".reveal").forEach((el) => {
      if (el.closest(".vf-route-source-snapshot")) return;
      const rect = el.getBoundingClientRect();
      if (rect.top < viewportH && rect.bottom > 0) list.push(el);
    });
    return list;
  };

  // Run inside the VT callback, before the new snapshot is captured. Static
  // pages have their own page/reveal entrance animations; snapshotting those at
  // opacity 0 exposed only the destination's blue/white background. Finish the
  // page fade and make the above-the-fold content fully visible so the browser
  // crossfades directly from the old page to a complete new page.
  const prepareInfoIntroForSnapshot = () => {
    if (typeof document === "undefined") return [];

    document.querySelectorAll('[class*="-page"]').forEach((el) => {
      if (el.closest(".vf-route-source-snapshot")) return;
      el.getAnimations().forEach((animation) => {
        if (
          animation.animationName &&
          /PageAppear$/.test(animation.animationName)
        ) {
          try {
            animation.finish();
          } catch (e) {
            /* already finished / cancelled */
          }
        }
      });
    });

    const reveals = inViewportReveals();
    reveals.forEach((el) => {
      el.classList.add("visible");
      el.style.setProperty("transition", "none", "important");
      el.style.setProperty("opacity", "1", "important");
      el.style.setProperty("transform", "none", "important");
    });
    void document.body.offsetHeight;
    return reveals;
  };

  // Remove only the temporary inline snapshot styles after the crossfade. The
  // visible class stays in place, so the live DOM cannot replay a second intro
  // or flash the page background after the old snapshot disappears.
  const releasePreparedIntro = (reveals) => {
    if (!reveals || !reveals.length) return;
    reveals.forEach((el) => {
      el.style.removeProperty("opacity");
      el.style.removeProperty("transform");
      el.style.removeProperty("transition");
    });
  };

  // Settle the freshly rendered page at the top and prepare its intro for the
  // snapshot, then resolve so the View Transition captures a clean "new" state.
  //
  // IMPORTANT: resolve on a microtask (Vue's nextTick), never requestAnimationFrame.
  // While a View Transition is awaiting this callback the browser suspends
  // rendering — so rAF callbacks don't fire and an rAF-based wait would deadlock
  // until the 4s VT timeout (the old snapshot freezes on screen that whole time).
  // Microtasks and timers still run, so nextTick resolves promptly; the setTimeout
  // is a hard safety cap so the transition can never hang.
  const settleAtTop = (state, stageInfoIntro, kind) =>
    new Promise((resolve) => {
      let settled = false;
      const finish = () => {
        if (settled) return;
        settled = true;
        window.scrollTo(0, 0);
        state.reveals = stageInfoIntro ? prepareInfoIntroForSnapshot() : [];
        resolve();
      };
      waitForMeaningfulDestination(kind, state.previousIdentity).then(finish);
      window.setTimeout(finish, ROUTE_DESTINATION_READY_MAX_MS);
    });

  const runSnapshotDissolveTransition = async (targetPath, targetKind) => {
    const root = document.documentElement;
    const snapshot = captureRouteSourceSnapshot();
    const state = {
      reveals: [],
      previousIdentity: routeContentIdentity(),
    };

    root.classList.add("vf-route-transition-active");
    root.dataset.vfRouteKind = targetKind;
    setRouteBusy(true);
    try {
      await commitRouteSourceSnapshot(snapshot);
      await router.push(targetPath);
      await settleAtTop(state, targetKind === "info", targetKind);
      await waitForCommittedRouteFrame();
      await fadeRouteSourceSnapshot(snapshot);
    } catch {
      await fadeRouteSourceSnapshot(snapshot);
    } finally {
      removeRouteSourceSnapshot();
      root.classList.remove("vf-route-transition-active");
      delete root.dataset.vfRouteKind;
      releasePreparedIntro(state.reveals);
      setRouteBusy(false);
      if (typeof router.__vfFocusDestination === "function") {
        router.__vfFocusDestination();
      }
    }
  };

  document.addEventListener(
    "click",
    (event) => {
      const targetPath = getCrossfadeTarget(event);
      if (!targetPath) return;

      const resolvedTarget = router.resolve(targetPath);
      const targetKind = classifyNavigation({
        pathname: resolvedTarget.path,
        routeName: resolvedTarget.name,
      });
      const currentRoute = router.currentRoute.value;
      const currentKind = classifyNavigation({
        pathname: currentRoute.path,
        routeName: currentRoute.name,
      });
      // Home and catalog controls already own their navigation. Do not cancel
      // their click and issue a second router.push from this capture listener.
      // Plain anchors retain their safe full-navigation fallback.
      if (shouldNavigateWithoutRouteTransition(currentKind, targetKind)) {
        return;
      }

      const shouldUseSnapshotDissolve = targetKind === "info";

      if (shouldUseSnapshotDissolve) {
        event.preventDefault();
        runSnapshotDissolveTransition(targetPath, targetKind).catch(() => {});
        return;
      }

      if (!supportsViewTransitions() || prefersReducedMotion()) return;

      // Take over navigation. The capture-phase preventDefault also makes
      // <router-link>'s own handler bail (it skips defaultPrevented events), so
      // we navigate exactly once — from inside the transition.
      event.preventDefault();

      const state = {
        reveals: [],
        // Do not treat the still-mounted previous footer/info component as the
        // destination. Vue can keep it alive while the next lazy chunk resolves.
        previousIdentity: routeContentIdentity(),
      };
      const root = document.documentElement;
      root.classList.add("vf-route-transition-active");
      root.dataset.vfRouteKind = targetKind;
      let transition;
      let slowTimer = 0;
      let navigationFinished = false;
      let transitionFinished = false;
      let released = false;
      let startNavigation;
      const navigationPromise = new Promise((resolve) => {
        startNavigation = () => {
          router
            .push(targetPath)
            .then(() => settleAtTop(state, isInfoPath(targetPath), targetKind))
            .catch(() => {})
            .then(resolve);
        };
      });
      try {
        transition = document.startViewTransition(() => {
          startNavigation();
          return navigationPromise;
        });
      } catch {
        root.classList.remove("vf-route-transition-active");
        delete root.dataset.vfRouteKind;
        router.push(targetPath).catch(() => {});
        return;
      }

      // Once the dissolve has fully finished, remove the temporary snapshot
      // styles while keeping the destination content visible.
      const releaseReveals = () => {
        if (released || !navigationFinished || !transitionFinished) return;
        released = true;
        if (slowTimer) window.clearTimeout(slowTimer);
        hideRouteLoadingCover();
        root.classList.remove("vf-route-transition-active");
        delete root.dataset.vfRouteKind;
        releasePreparedIntro(state.reveals);
        if (typeof router.__vfFocusDestination === "function") {
          router.__vfFocusDestination();
        }
      };
      // Info pages are local route chunks. Keep the source snapshot visible
      // until their complete content is ready instead of replacing it with a
      // white loading cover. Slower API-backed destinations retain the neutral
      // safety cover.
      if (targetKind !== "info" && shouldUseRouteLoadingCover(targetKind)) {
        slowTimer = window.setTimeout(() => {
          showRouteLoadingCover();
          if (typeof transition.skipTransition === "function") {
            transition.skipTransition();
          }
        }, ROUTE_TRANSITION_SLOW_MS);
      }
      navigationPromise.then(
        () => {
          navigationFinished = true;
          releaseReveals();
        },
        () => {
          navigationFinished = true;
          releaseReveals();
        }
      );
      transition.finished.then(
        () => {
          transitionFinished = true;
          releaseReveals();
        },
        () => {
          transitionFinished = true;
          releaseReveals();
        }
      );
    },
    true // capture phase: run before <router-link> and before any scroll
  );
}

// ────────────────────────────────────────────────────────────────────────────
// GA4 virtual page_view tracking for client-side (SPA) navigations. This is an
// SPA — vue-router swaps pages without a full reload, so GTM's GA4 config tag
// only ever sees the initial (server-rendered) request. The afterEach hook
// below pushes a synthetic page_view-style event for every route change *after*
// that.
//
// The hook is registered only once router.isReady() has resolved. isReady()
// waits for the router's initial navigation (the one matching the URL the page
// was loaded/hydrated with) to fully settle before resolving — so by the time
// afterEach is attached, that first navigation's guards have already run and
// afterEach will only ever observe *subsequent* navigations. This avoids
// double-counting the first pageview, which GTM's GA4 config tag already
// tracks, without needing a manual "is this the first call" flag.
// ────────────────────────────────────────────────────────────────────────────
function setupSpaPageViewTracking(router) {
  if (typeof window === "undefined") return;

  let pendingPageView = 0;

  router.isReady().then(() => {
    router.afterEach((to, from, failure) => {
      if (failure) return;
      if (!window.dataLayer) return;

      // @vueuse/head applies document.title asynchronously after navigation;
      // a small delay lets it settle before we read it.
      window.clearTimeout(pendingPageView);
      pendingPageView = window.setTimeout(() => {
        if (!window.dataLayer) return;
        if (router.currentRoute.value.fullPath !== to.fullPath) return;
        window.dataLayer.push({
          event: "spa_page_view",
          page_path: to.fullPath,
          page_location: window.location.href,
          page_title: document.title,
          page_referrer: window.location.origin + from.fullPath,
        });
      }, 100);
    });
  });
}

createApp().then(async ({ app, router }) => {
  await router.isReady();
  let initialHydrationComplete = false;
  app.provide("onInitialHydration", () => {
    if (initialHydrationComplete) return;
    initialHydrationComplete = true;
    // Async route setup and nested Suspense must finish before components
    // can switch to their subsequent-navigation lazy rendering policy.
    markHydrated();
    markAnalyticsReady();
    document.querySelector("#app")?.setAttribute("data-v-app", "");
  });
  if (typeof window !== "undefined") {
    const origin = window.location.origin;
    app.config.globalProperties.$origin = origin;
    app.provide("$origin", origin);
    setupProgrammaticNavigationTransitions(router);
    setupLuxuryPageCrossfade(router);
    setupSpaPageViewTracking(router);
  }
  app.mount("#app");
});
